var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
/*
 * <vitesse-logo> — the real Vitesse mark, ported from public/logo3.svg (full
 * wordmark) + public/logo.svg (compact mark). The mark carries a continuous
 * blue gradient (the same coloration as the loading splash) via an in-SVG
 * <linearGradient> in userSpaceOnUse, so the fade spans the WHOLE wordmark
 * rather than per-glyph, and reads identically on light + dark themes (instead
 * of flipping black↔white like the agent's swapped -dark.svg files). The
 * gradient id is shadow-DOM-scoped, so it never collides across instances.
 * `compact` renders just the V/A arrow mark.
 */
let VitesseLogo = class VitesseLogo extends LitElement {
    constructor() {
        super(...arguments);
        this.compact = false;
        /** Render ONLY the "vitesse" wordmark text (no V/A mark) — used in the header. */
        this.textOnly = false;
    }
    updated() {
        const svg = this.renderRoot.querySelector('svg');
        if (!svg)
            return;
        if (this.size) {
            // EXPLICIT contain-fit dimensions from the variant's viewBox aspect — `auto` sizing
            // collapses to 0 for an SVG (no intrinsic size) inside a flex row, so never use it.
            const aspect = this.compact ? 512 / 258.9 : this.textOnly ? 471.7 / 76.8 : 898.5 / 185;
            const w = aspect >= 1 ? this.size : this.size * aspect;
            const h = aspect >= 1 ? this.size / aspect : this.size;
            svg.style.width = `${w.toFixed(1)}px`;
            svg.style.height = `${h.toFixed(1)}px`;
        }
        else {
            svg.style.width = '';
            svg.style.height = '';
        }
    }
    static { this.styles = css `
    :host {
      display: inline-flex;
      line-height: 0;
      transition: filter 0.2s ease;
    }
    :host(:hover) {
      filter: brightness(0.82); /* darken on hover, not lighten */
    }
    svg {
      height: 32px;
      width: auto;
      display: block;
    }
    /* Text-only wordmark: the viewBox is the tight glyph bbox, so this height IS
       the visible text height — sized to match the tree-toggle glyph next to it. */
    svg.text-only {
      height: 16px;
    }
  `; }
    // The brand-blue fade — deep blue → azure → sky, echoing the splash gradient.
    // userSpaceOnUse spans each variant's full viewBox so it's ONE continuous fade
    // across every glyph, not a per-shape repeat. The <defs> are inlined as STATIC
    // svg content (NOT an interpolated nested html`` template) on purpose: Lit's
    // `html` tag builds interpolated children in the HTML namespace, so an
    // interpolated <linearGradient> would be an inert HTML element and the
    // fill="url(#…)" would paint nothing. Inlined, the browser parses it as SVG.
    render() {
        if (this.textOnly)
            // Just the "vitesse" letters. viewBox is the TIGHT glyph bbox (x 426.8→898.5,
            // y 46.1→122.9) so the rendered svg height equals the visible text height —
            // sized small (see the height: rule below) to match the tree-toggle glyph.
            return html `<svg
        class="text-only"
        viewBox="426.8 46.1 471.7 76.8"
        fill="url(#vitesse-logo-grad)"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <linearGradient id="vitesse-logo-grad" gradientUnits="userSpaceOnUse" x1="426.8" y1="46.1" x2="898.5" y2="122.9">
            <stop offset="0" stop-color="#1652c9" />
            <stop offset="0.55" stop-color="#2491db" />
            <stop offset="1" stop-color="#6fb2f4" />
          </linearGradient>
        </defs>
        <polygon points="426.8 46.2 446 46.2 473.6 122.9 454.3 122.9 426.8 46.2" />
        <polygon points="503.8 46.2 484.6 46.2 467.8 88.6 477 114.3 503.8 46.2" />
        <rect x="514.8" y="46.2" width="14.4" height="76.7" />
        <rect x="566.8" y="66.9" width="14.4" height="55.8" />
        <rect x="545.5" y="46.1" width="57" height="14.9" />
        <polygon points="633.1 91.9 669.9 91.9 669.9 77 633.1 77 633.1 66.9 618.6 66.9 618.6 77 618.6 91.9 618.6 107.9 618.6 122.8 633.1 122.8 675.6 122.8 675.6 107.9 633.1 107.9 633.1 91.9" />
        <rect x="618.6" y="46.1" width="57" height="14.9" />
        <polygon points="856 91.9 892.8 91.9 892.8 77 856 77 856 66.9 841.5 66.9 841.5 77 841.5 91.9 841.5 107.9 841.5 122.8 856 122.8 898.5 122.8 898.5 107.9 856 107.9 856 91.9" />
        <rect x="841.5" y="46.1" width="57" height="14.9" />
        <path d="M691.7,61v-7.4c0-4.1,3.4-7.5,7.5-7.5h49.7v14.9h-57.2Z" />
        <path
          d="M738.8,77.1h-29.2c-1.7,0-3.1-1.4-3.1-3.1v-7.4h-14.6v17.2c0,4.3,3.5,7.8,7.8,7.8h31.3c1.7,0,3,1.3,3,3v10.2c0,1.7-1.3,3-3,3h-39.3v14.9h49.7c4.1,0,7.5-3.4,7.5-7.5h0v-30.7c0-4.1-3.4-7.5-7.5-7.5h-2.6Z"
        />
        <path d="M766.6,61v-7.4c0-4.1,3.4-7.5,7.5-7.5h49.7v14.9h-57.2Z" />
        <path
          d="M813.7,77.1h-29.2c-1.7,0-3.1-1.4-3.1-3.1v-7.4h-14.6v17.2c0,4.3,3.5,7.8,7.8,7.8h31.3c1.7,0,3,1.3,3,3v10.2c0,1.7-1.3,3-3,3h-39.3v14.9h49.7c4.1,0,7.5-3.4,7.5-7.5h0v-30.7c0-4.1-3.4-7.5-7.5-7.5h-2.6Z"
        />
      </svg>`;
        return this.compact
            ? html `<svg viewBox="0 0 512 258.9" fill="url(#vitesse-logo-grad)" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="vitesse-logo-grad" gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="512" y2="258.9">
              <stop offset="0" stop-color="#1652c9" />
              <stop offset="0.55" stop-color="#2491db" />
              <stop offset="1" stop-color="#6fb2f4" />
            </linearGradient>
          </defs>
          <polygon points="126.08 0 190.85 0 283.98 258.9 218.66 258.9 126.08 0" />
          <polygon points="385.92 0 321.15 0 264.5 143.24 295.44 229.99 385.92 0" />
          <polygon points="140.94 107.58 125.53 64.5 0 64.5 0 107.58 140.94 107.58" />
          <polygon points="148.56 128.9 0 128.9 0 171.99 163.97 171.99 148.56 128.9" />
          <polygon points="383.39 64.5 366.43 107.58 512 107.58 512 64.5 383.39 64.5" />
          <polygon points="358.05 128.9 341.1 171.99 512 171.99 512 128.9 358.05 128.9" />
        </svg>`
            : html `<svg viewBox="0 0 898.5 185" fill="url(#vitesse-logo-grad)" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="vitesse-logo-grad" gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="898.5" y2="185">
              <stop offset="0" stop-color="#1652c9" />
              <stop offset="0.55" stop-color="#2491db" />
              <stop offset="1" stop-color="#6fb2f4" />
            </linearGradient>
          </defs>
          <polygon points="90.1 0 136.4 0 202.9 185 156.2 185 90.1 0" />
          <polygon points="275.8 0 229.5 0 189 102.4 211.1 164.3 275.8 0" />
          <polygon points="100.7 76.9 89.7 46.1 0 46.1 0 76.9 100.7 76.9" />
          <polygon points="274 46.1 261.8 76.9 365.9 76.9 365.9 46.1 274 46.1" />
          <polygon points="106.2 92.1 0 92.1 0 122.9 117.2 122.9 106.2 92.1" />
          <polygon points="255.9 92.1 243.7 122.9 365.9 122.9 365.9 92.1 255.9 92.1" />
          <polygon points="426.8 46.2 446 46.2 473.6 122.9 454.3 122.9 426.8 46.2" />
          <polygon points="503.8 46.2 484.6 46.2 467.8 88.6 477 114.3 503.8 46.2" />
          <rect x="514.8" y="46.2" width="14.4" height="76.7" />
          <rect x="566.8" y="66.9" width="14.4" height="55.8" />
          <rect x="545.5" y="46.1" width="57" height="14.9" />
          <polygon points="633.1 91.9 669.9 91.9 669.9 77 633.1 77 633.1 66.9 618.6 66.9 618.6 77 618.6 91.9 618.6 107.9 618.6 122.8 633.1 122.8 675.6 122.8 675.6 107.9 633.1 107.9 633.1 91.9" />
          <rect x="618.6" y="46.1" width="57" height="14.9" />
          <polygon points="856 91.9 892.8 91.9 892.8 77 856 77 856 66.9 841.5 66.9 841.5 77 841.5 91.9 841.5 107.9 841.5 122.8 856 122.8 898.5 122.8 898.5 107.9 856 107.9 856 91.9" />
          <rect x="841.5" y="46.1" width="57" height="14.9" />
          <path d="M691.7,61v-7.4c0-4.1,3.4-7.5,7.5-7.5h49.7v14.9h-57.2Z" />
          <path d="M738.8,77.1h-29.2c-1.7,0-3.1-1.4-3.1-3.1v-7.4h-14.6v17.2c0,4.3,3.5,7.8,7.8,7.8h31.3c1.7,0,3,1.3,3,3v10.2c0,1.7-1.3,3-3,3h-39.3v14.9h49.7c4.1,0,7.5-3.4,7.5-7.5h0v-30.7c0-4.1-3.4-7.5-7.5-7.5h-2.6Z" />
          <path d="M766.6,61v-7.4c0-4.1,3.4-7.5,7.5-7.5h49.7v14.9h-57.2Z" />
          <path d="M813.7,77.1h-29.2c-1.7,0-3.1-1.4-3.1-3.1v-7.4h-14.6v17.2c0,4.3,3.5,7.8,7.8,7.8h31.3c1.7,0,3,1.3,3,3v10.2c0,1.7-1.3,3-3,3h-39.3v14.9h49.7c4.1,0,7.5-3.4,7.5-7.5h0v-30.7c0-4.1-3.4-7.5-7.5-7.5h-2.6Z" />
        </svg>`;
    }
};
__decorate([
    property({ type: Boolean })
], VitesseLogo.prototype, "compact", void 0);
__decorate([
    property({ type: Boolean })
], VitesseLogo.prototype, "textOnly", void 0);
__decorate([
    property({ type: Number })
], VitesseLogo.prototype, "size", void 0);
VitesseLogo = __decorate([
    customElement('vitesse-logo')
], VitesseLogo);
export { VitesseLogo };
//# sourceMappingURL=vitesse-logo.js.map